/**
 * Country Picker Modal
 * Searchable list of countries with flag icons.
 * Follows the FiatCurrencyModal pattern.
 */

import { useState, useMemo } from 'react';
import { CloseButton } from '../ui/CloseButton';
import { SearchInput } from '../ui/SearchInput';
import { NRLogo } from '../ui/NRLogo';
import { COUNTRIES, COUNTRY_MAP } from '../../data/countries';
import { useT } from '../../i18n';

export interface CountryPickerModalProps {
  onClose: () => void;
  onSelect: (countryCode: string) => void;
  /** Base URL for static assets (flags) */
  assetBaseUrl?: string;
  /** Currently selected country code */
  selectedCode?: string;
}

export function CountryPickerModal({
  onClose,
  onSelect,
  assetBaseUrl = '',
  selectedCode,
}: CountryPickerModalProps) {
  const t = useT();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const raw = search.trim().toLowerCase();

    // If no search, put selected item first
    if (!raw) {
      if (selectedCode) {
        const normalizedCode = selectedCode.toLowerCase();
        const selected = COUNTRY_MAP[normalizedCode];
        if (selected) {
          const rest = COUNTRIES.filter((c) => c.code !== normalizedCode);
          return [selected, ...rest];
        }
      }
      return COUNTRIES;
    }

    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(raw) ||
        c.code.includes(raw) ||
        c.defaultFiat.toLowerCase().includes(raw)
    );
  }, [search, selectedCode]);

  return (
    <>
      {/* Modal header — 128px with blurred bg */}
      <div className="relative shrink-0" style={{ height: 128, background: 'var(--t-header-bg)', backdropFilter: 'blur(var(--t-header-blur))', WebkitBackdropFilter: 'blur(var(--t-header-blur))' }}>
        <div className="absolute" style={{ left: 12, top: 20 }}>
          <NRLogo width={46} />
        </div>
        <p className="absolute text-[16px] text-center truncate" style={{ top: 28, left: 64, right: 64, color: 'var(--t-text)', lineHeight: '24px' }}>
          {t('modal.selectCountry')}
        </p>
        <div className="absolute" style={{ right: 12, top: 20 }}>
          <CloseButton onClick={onClose} />
        </div>
        <div className="absolute" style={{ bottom: 8, left: 12, right: 12 }}>
          <SearchInput value={search} onChange={setSearch} inline />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scroll px-3">
        {filtered.map((c, i) => {
          const isSelected = selectedCode?.toLowerCase() === c.code;
          return (
            <button
              key={c.code}
              onClick={() => onSelect(c.code)}
              className="flex items-center w-full card-press hover-item modal-item-in"
              style={{ gap: 16, animationDelay: `${Math.min(i * 45, 350)}ms` }}
            >
              <CountryFlag code={c.code} size={32} baseUrl={assetBaseUrl} />
              <div className="flex-1 flex items-center" style={{ borderBottom: '1px solid var(--t-card-border)', paddingTop: 20, paddingBottom: 20, gap: 10 }}>
                <span className="flex-1 text-[18px] text-left" style={{ color: 'var(--t-text)', lineHeight: '20px' }}>
                  {c.name}
                </span>
                {isSelected && (
                  <div style={{ width: 40, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.5 4.5L6 12L2.5 8.5"
                        stroke="var(--t-success, #4CD964)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-10 text-[14px]" style={{ color: 'var(--t-text-muted)' }}>
            {t('modal.noCountries')}
          </div>
        )}
      </div>
    </>
  );
}

/** Country flag image using the /flags/{code}.svg pattern */
function CountryFlag({ code, size = 32, baseUrl = '' }: { code: string; size?: number; baseUrl?: string }) {
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'var(--t-pill-bg, #333)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size * 0.4,
          color: 'var(--t-text-muted)',
        }}
      >
        {code.toUpperCase().slice(0, 2)}
      </div>
    );
  }

  return (
    <img
      src={`${baseUrl}/flags/${code}.svg`}
      alt={code}
      width={size}
      height={size}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
      onError={() => setError(true)}
    />
  );
}
